(function () {
  const DEFAULT_API_BASE = 'https://diggnitcustomersupport.floot.app/_api/reviews/storefront';
  const MAX_UPLOADS = 4;
  const MAX_FILE_BYTES = 5 * 1024 * 1024;

  function unwrapPayload(payload) {
    if (payload && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'json')) {
      return payload.json;
    }

    return payload;
  }

  async function requestJson(url, options) {
    const response = await fetch(url, options);
    const rawText = await response.text();
    let parsed = {};

    if (rawText) {
      try {
        parsed = unwrapPayload(JSON.parse(rawText));
      } catch (error) {
        parsed = {};
      }
    }

    if (!response.ok) {
      const errorMessage =
        (parsed && parsed.error) ||
        (parsed && parsed.message) ||
        'Request failed';
      throw new Error(errorMessage);
    }

    return parsed;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function decodeHtmlEntities(value) {
    const input = String(value || '');
    if (!input || input.indexOf('&') === -1 || typeof document === 'undefined') {
      return input;
    }

    const textarea = document.createElement('textarea');
    textarea.innerHTML = input;
    return textarea.value;
  }

  function replaceTemplate(template, replacements) {
    let output = template;

    Object.keys(replacements).forEach(function (key) {
      output = output.replace(new RegExp('{{\\s*' + key + '\\s*}}', 'g'), replacements[key]);
      output = output.replace(new RegExp('__' + key + '__', 'g'), replacements[key]);
    });

    return output;
  }

  function normaliseDistribution(distribution) {
    const result = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    if (Array.isArray(distribution)) {
      distribution.forEach(function (item) {
        const rating = Number(item.rating);
        if (rating >= 1 && rating <= 5) {
          result[rating] = Number(item.count || 0);
        }
      });

      return result;
    }

    if (distribution && typeof distribution === 'object') {
      Object.keys(distribution).forEach(function (key) {
        const rating = Number(key);
        if (rating >= 1 && rating <= 5) {
          result[rating] = Number(distribution[key] || 0);
        }
      });
    }

    return result;
  }

  function buildStars(rating) {
    const whole = Math.max(0, Math.min(5, Number(rating || 0)));
    let markup = '';
    let index = 0;

    while (index < 5) {
      markup += index < whole ? '★' : '☆';
      index += 1;
    }

    return markup;
  }

  function formatDate(value, locale) {
    if (!value) {
      return '';
    }

    try {
      return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(new Date(value));
    } catch (error) {
      return value;
    }
  }

  function localeLabel(locale, strings) {
    if ((locale || '').toLowerCase().indexOf('ar') === 0) {
      return strings.languageArabic;
    }

    return strings.languageEnglish;
  }

  function getStrings(section) {
    const node = section.querySelector('[data-diggn-review-strings]');
    if (!node) {
      return {};
    }

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  function fileToBase64(file) {
    return new Promise(function (resolve, reject) {
      const reader = new FileReader();

      reader.onload = function () {
        const result = String(reader.result || '');
        resolve(result.split(',')[1] || '');
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function DiggnReviews(section) {
    this.section = section;
    this.productId = section.dataset.productId;
    this.productHandle = section.dataset.productHandle;
    this.locale = section.dataset.locale || document.documentElement.lang || 'en';
    this.apiBase = section.dataset.apiBase || DEFAULT_API_BASE;
    this.strings = Object.assign(
      {
        reviewCountLabel: '{{ count }} reviews',
        verifiedBuyerCountLabel: 'Feedback from {{ count }} customers',
        mediaReviewCountLabel: '{{ count }} photo reviews',
        aiGeneratedSummaryLabel: 'From customer reviews',
        loading: 'Loading reviews...',
        unavailable: 'Reviews are temporarily unavailable.',
        migrationMessage: 'Detailed reviews are still being moved over. Rating totals are already live.',
        noReviews: 'No reviews yet.',
        noReviewsPrompt: 'Be the first to review this product.',
        verifiedBuyer: 'Customer review',
        diggnReply: "Diggn'It reply",
        languageEnglish: 'English',
        languageArabic: 'Arabic',
        featuredReview: 'Featured review',
        translateToEnglish: 'Translate to English',
        translateToArabic: 'Translate to Arabic',
        showOriginal: 'Show original',
        translating: 'Translating...',
        translationError: 'Translation is temporarily unavailable.',
        loadMore: 'Load more reviews',
        submitSuccess: 'Thanks for your review. It is now pending moderation.',
        submitError: 'We could not submit your review. Please try again.',
        formFileLimit: 'Please upload up to 4 photos only.',
        formFileSize: 'Each photo must be 5MB or smaller.',
      },
      getStrings(section)
    );

    this.proof = document.querySelector('[data-diggn-review-proof][data-product-id="' + this.productId + '"]');
    this.state = {
      page: 1,
      language: 'all',
      rating: 'all',
      sort: 'helpful',
      withPhotos: false,
      hasMore: false,
      reviews: [],
      summary: null,
      translations: {},
    };

    this.summaryStatus = section.querySelector('[data-diggn-reviews-summary-status]');
    this.featured = section.querySelector('[data-diggn-featured-review]');
    this.scoreValue = section.querySelector('[data-diggn-score]');
    this.scoreStars = section.querySelector('[data-diggn-score-stars]');
    this.scoreCount = section.querySelector('[data-diggn-score-count]');
    this.proofLine = section.querySelector('[data-diggn-proof-line]');
    this.reviewList = section.querySelector('[data-diggn-review-list]');
    this.resultsStatus = section.querySelector('[data-diggn-reviews-status]');
    this.loadMoreButton = section.querySelector('[data-diggn-load-more]');
    this.languageFilter = section.querySelector('[data-diggn-filter-language]');
    this.ratingFilter = section.querySelector('[data-diggn-filter-rating]');
    this.sortFilter = section.querySelector('[data-diggn-filter-sort]');
    this.photoFilter = section.querySelector('[data-diggn-filter-photos]');
    this.formPanel = section.querySelector('[data-diggn-review-form-panel]');
    this.form = section.querySelector('[data-diggn-review-form]');
    this.formStatus = section.querySelector('[data-diggn-review-form-status]');
    this.toggleButtons = document.querySelectorAll(
      '[data-diggn-write-review][data-product-id="' + this.productId + '"]'
    );

    this.setDirection();
    this.bind();
    this.init();
  }

  DiggnReviews.prototype.setDirection = function () {
    const isArabic = String(this.locale || '').toLowerCase().indexOf('ar') === 0;
    this.section.setAttribute('dir', isArabic ? 'rtl' : 'ltr');

    if (this.proof) {
      this.proof.setAttribute('dir', isArabic ? 'rtl' : 'ltr');
    }
  };

  DiggnReviews.prototype.endpoint = function (path, params) {
    const url = new URL(this.apiBase.replace(/\/$/, '') + '/' + path.replace(/^\//, ''));

    Object.keys(params || {}).forEach(function (key) {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '' && value !== false) {
        url.searchParams.set(key, value);
      }
    });

    return url.toString();
  };

  DiggnReviews.prototype.bind = function () {
    const self = this;

    if (this.languageFilter) {
      this.languageFilter.addEventListener('change', function () {
        self.state.language = self.languageFilter.value;
        self.fetchReviews(true);
      });
    }

    if (this.ratingFilter) {
      this.ratingFilter.addEventListener('change', function () {
        self.state.rating = self.ratingFilter.value;
        self.fetchReviews(true);
      });
    }

    if (this.sortFilter) {
      this.sortFilter.addEventListener('change', function () {
        self.state.sort = self.sortFilter.value;
        self.fetchReviews(true);
      });
    }

    if (this.photoFilter) {
      this.photoFilter.addEventListener('change', function () {
        self.state.withPhotos = self.photoFilter.checked;
        self.fetchReviews(true);
      });
    }

    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener('click', function () {
        self.fetchReviews(false);
      });
    }

    if (this.reviewList) {
      this.reviewList.addEventListener('click', function (event) {
        const actionButton = event.target.closest('[data-diggn-translate-action]');
        if (!actionButton) {
          return;
        }

        event.preventDefault();

        const reviewId = Number(actionButton.getAttribute('data-review-id'));
        const action = actionButton.getAttribute('data-diggn-translate-action');

        if (!reviewId) {
          return;
        }

        if (action === 'translate') {
          self.translateReview(reviewId);
        } else if (action === 'original') {
          self.showOriginalReview(reviewId);
        }
      });
    }

    if (this.form) {
      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
        self.submitReview();
      });
    }

    this.toggleButtons.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        self.openForm();
      });
    });
  };

  DiggnReviews.prototype.init = function () {
    this.fetchSummary();
    this.fetchReviews(true);
  };

  DiggnReviews.prototype.pageLocaleCode = function () {
    return String(this.locale || '').toLowerCase().indexOf('ar') === 0 ? 'ar' : 'en';
  };

  DiggnReviews.prototype.getTranslateTargetLocale = function () {
    return this.pageLocaleCode();
  };

  DiggnReviews.prototype.getTranslateButtonLabel = function () {
    return this.getTranslateTargetLocale() === 'ar'
      ? this.strings.translateToArabic
      : this.strings.translateToEnglish;
  };

  DiggnReviews.prototype.shouldOfferTranslation = function (review) {
    if (!review || !review.id || !review.locale) {
      return false;
    }

    return String(review.locale).toLowerCase().indexOf(this.getTranslateTargetLocale()) !== 0;
  };

  DiggnReviews.prototype.translateReview = async function (reviewId) {
    const existingState = this.state.translations[reviewId];

    if (existingState && existingState.translatedBody) {
      this.state.translations[reviewId] = Object.assign({}, existingState, {
        showingTranslation: true,
        error: '',
      });
      this.renderReviewList();
      return;
    }

    this.state.translations[reviewId] = Object.assign({}, existingState, {
      loading: true,
      showingTranslation: false,
      error: '',
    });
    this.renderReviewList();

    try {
      const payload = await requestJson(this.endpoint('translate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify({
          reviewId: reviewId,
          targetLocale: this.getTranslateTargetLocale(),
        }),
      });

      this.state.translations[reviewId] = {
        loading: false,
        showingTranslation: true,
        translatedTitle: payload.title || null,
        translatedBody: payload.body || '',
        label: payload.label || '',
        error: '',
      };
    } catch (error) {
      this.state.translations[reviewId] = Object.assign({}, existingState, {
        loading: false,
        showingTranslation: false,
        error: (error && error.message) || this.strings.translationError,
      });
    }

    this.renderReviewList();
  };

  DiggnReviews.prototype.showOriginalReview = function (reviewId) {
    const existingState = this.state.translations[reviewId];
    if (!existingState) {
      return;
    }

    this.state.translations[reviewId] = Object.assign({}, existingState, {
      showingTranslation: false,
      error: '',
    });
    this.renderReviewList();
  };

  DiggnReviews.prototype.fetchSummary = async function () {
    this.setSummaryStatus(this.strings.loading, false);

    try {
      const payload = await requestJson(
        this.endpoint('summary', {
          productId: this.productId,
          productHandle: this.productHandle,
          locale: this.locale,
        })
      );

      this.state.summary = payload;
      this.renderSummary(payload);
      this.setSummaryStatus('', false);
    } catch (error) {
      this.setSummaryStatus(this.strings.migrationMessage || this.strings.unavailable, true);
      if (this.proof) {
        const summaryNode = this.proof.querySelector('[data-diggn-proof-summary]');
        if (summaryNode) {
          summaryNode.textContent = this.strings.migrationMessage || this.strings.unavailable;
        }
      }
    }
  };

  DiggnReviews.prototype.fetchReviews = async function (reset) {
    if (reset) {
      this.state.page = 1;
      this.state.reviews = [];
      if (this.reviewList) {
        this.reviewList.innerHTML = '';
      }
      this.setResultsStatus(this.strings.loading, false);
    }

    try {
      const payload = await requestJson(
        this.endpoint('list', {
          productId: this.productId,
          productHandle: this.productHandle,
          locale: this.locale,
          language: this.state.language,
          rating: this.state.rating === 'all' ? '' : this.state.rating,
          sort: this.state.sort === 'media' ? 'helpful' : this.state.sort,
          hasMedia: this.state.withPhotos || this.state.sort === 'media' ? 'true' : '',
          page: this.state.page,
          limit: 6,
        })
      );

      const incomingReviews = Array.isArray(payload.reviews) ? payload.reviews : [];
      this.state.reviews = reset ? incomingReviews : this.state.reviews.concat(incomingReviews);
      this.state.hasMore = Boolean(payload.hasMore);
      this.state.page = Number(payload.page || this.state.page) + 1;
      this.renderReviewList();
      this.setResultsStatus('', false);
    } catch (error) {
      this.setResultsStatus(this.strings.unavailable, true);
    }
  };

  DiggnReviews.prototype.renderSummary = function (summary) {
    const reviewCount = Number(summary.reviewCount || 0);
    const verifiedBuyerCount = Number(summary.verifiedBuyerCount || 0);
    const mediaReviewCount = Number(summary.mediaReviewCount || 0);
    const averageRating = Number(summary.avgRating || 0);
    const proofSummary = summary.proofSummary || this.strings.noReviewsPrompt;
    const distribution = normaliseDistribution(summary.distribution);
    const total = reviewCount || 1;
    const proofOutcomes = Array.isArray(summary.topOutcomes) ? summary.topOutcomes : [];

    if (this.scoreValue) {
      this.scoreValue.textContent = averageRating > 0 ? averageRating.toFixed(1) : '0.0';
    }

    if (this.scoreStars) {
      const decimal = averageRating % 1;
      let ratingDecimal = 0;

      if (decimal >= 0.3 && decimal <= 0.7) {
        ratingDecimal = 0.5;
      } else if (decimal > 0.7) {
        ratingDecimal = 1;
      }

      this.scoreStars.style.setProperty('--rating', Math.floor(averageRating));
      this.scoreStars.style.setProperty('--rating-max', 5);
      this.scoreStars.style.setProperty('--rating-decimal', ratingDecimal);
    }

    if (this.scoreCount) {
      this.scoreCount.textContent = replaceTemplate(this.strings.reviewCountLabel, {
        count: reviewCount,
      });
    }

    if (this.proofLine) {
      this.proofLine.textContent = proofSummary;
    }

    if (this.proof) {
      const summaryNode = this.proof.querySelector('[data-diggn-proof-summary]');
      const aiLabelNode = this.proof.querySelector('[data-diggn-proof-ai-label]');
      const aiLabelTextNode = this.proof.querySelector('[data-diggn-proof-ai-label-text]');
      const countNode = this.proof.querySelector('[data-diggn-proof-count]');
      const verifiedNode = this.proof.querySelector('[data-diggn-proof-verified]');
      const mediaNode = this.proof.querySelector('[data-diggn-proof-media]');
      const chipList = this.proof.querySelector('[data-diggn-proof-chips]');

      if (aiLabelNode) {
        aiLabelNode.hidden = !summary.isAiGenerated;
      }

      if (aiLabelTextNode) {
        aiLabelTextNode.textContent = this.strings.aiGeneratedSummaryLabel;
      }

      if (summaryNode) {
        summaryNode.textContent = reviewCount > 0 ? proofSummary : this.strings.noReviewsPrompt;
      }

      if (countNode) {
        countNode.textContent = replaceTemplate(this.strings.reviewCountLabel, {
          count: reviewCount,
        });
      }

      if (verifiedNode) {
        verifiedNode.hidden = true;
        verifiedNode.textContent = '';
      }

      if (mediaNode) {
        mediaNode.hidden = mediaReviewCount <= 0;
        mediaNode.textContent = replaceTemplate(this.strings.mediaReviewCountLabel, {
          count: mediaReviewCount,
        });
      }

      if (chipList) {
        chipList.innerHTML = '';
        proofOutcomes.slice(0, 3).forEach(function (outcome) {
          const chip = document.createElement('span');
          chip.className = 'diggn-review-proof__chip';
          chip.textContent = outcome.label || outcome.key || '';
          chipList.appendChild(chip);
        });
      }
    }

    [5, 4, 3, 2, 1].forEach(
      function (rating) {
        const row = this.section.querySelector('[data-diggn-distribution="' + rating + '"]');
        if (!row) {
          return;
        }

        const count = Number(distribution[rating] || 0);
        const fill = row.querySelector('[data-diggn-distribution-fill]');
        const value = row.querySelector('[data-diggn-distribution-value]');

        if (fill) {
          fill.style.width = ((count / total) * 100).toFixed(2) + '%';
        }

        if (value) {
          value.textContent = count;
        }
      }.bind(this)
    );

    this.renderFeaturedReview(summary.featuredReview);
  };

  DiggnReviews.prototype.renderFeaturedReview = function (featuredReview) {
    if (!this.featured) {
      return;
    }

    if (!featuredReview || !featuredReview.body) {
      this.featured.hidden = true;
      return;
    }

    this.featured.hidden = false;
    this.featured.innerHTML =
      '<p class="diggn-reviews__eyebrow">' +
      escapeHtml(this.strings.featuredReview) +
      '</p>' +
      '<div class="diggn-reviews__stars" aria-hidden="true">' +
      buildStars(featuredReview.rating) +
      '</div>' +
      '<p class="diggn-reviews__featured-quote">"' +
      escapeHtml(featuredReview.body) +
      '"</p>' +
      '<p class="diggn-reviews__featured-author">' +
      escapeHtml(featuredReview.authorName || '') +
      '</p>';
  };

  DiggnReviews.prototype.renderReviewList = function () {
    if (!this.reviewList) {
      return;
    }

    if (!this.state.reviews.length) {
      this.reviewList.innerHTML =
        '<div class="diggn-reviews__empty">' +
        escapeHtml(this.strings.noReviews) +
        '<br>' +
        escapeHtml(this.strings.noReviewsPrompt) +
        '</div>';
    } else {
      this.reviewList.innerHTML = this.state.reviews
        .map(
          function (review) {
            const media = Array.isArray(review.media) ? review.media : [];
            const hasReply = review.reply && review.reply.body;
            const badgeLocale = review.localeLabel || localeLabel(review.locale, this.strings);
            const createdAt = formatDate(review.createdAt, this.locale);
            const translationState = this.state.translations[review.id] || null;
            const showTranslated =
              Boolean(translationState && translationState.showingTranslation && translationState.translatedBody);
            const displayTitle = showTranslated
              ? translationState.translatedTitle || review.title || ''
              : review.title || '';
            const displayBody = showTranslated
              ? translationState.translatedBody || review.body || ''
              : review.body || '';
            const translationLabel = showTranslated && translationState.label ? translationState.label : '';
            const canTranslate = this.shouldOfferTranslation(review);
            const isTranslating = Boolean(translationState && translationState.loading);
            const translationError =
              translationState && translationState.error && !showTranslated
                ? translationState.error
                : '';
            const safeTitle = escapeHtml(decodeHtmlEntities(displayTitle)).replace(/\n/g, '<br>');
            const safeBody = escapeHtml(decodeHtmlEntities(displayBody)).replace(/\n/g, '<br>');
            const safeAuthor = escapeHtml(decodeHtmlEntities(review.authorName || ''));
            const safeReply = hasReply
              ? escapeHtml(decodeHtmlEntities(review.reply.body || '')).replace(/\n/g, '<br>')
              : '';

            return (
              '<article class="diggn-reviews__card">' +
              '<div class="diggn-reviews__card-head">' +
              '<div>' +
              '<div class="diggn-reviews__author">' +
              safeAuthor +
              '</div>' +
              '<div class="diggn-reviews__badges">' +
              '<span class="diggn-reviews__badge">' +
              escapeHtml(decodeHtmlEntities(badgeLocale)) +
              '</span>' +
              '</div>' +
              '</div>' +
              '<div class="diggn-reviews__meta">' +
              '<div class="diggn-reviews__stars" aria-hidden="true">' +
              buildStars(review.rating) +
              '</div>' +
              (createdAt ? '<div>' + escapeHtml(createdAt) + '</div>' : '') +
              '</div>' +
              '</div>' +
              (safeTitle ? '<h3 class="diggn-reviews__title-small">' + safeTitle + '</h3>' : '') +
              '<p class="diggn-reviews__body">' +
              safeBody +
              '</p>' +
              (translationLabel
                ? '<p class="diggn-reviews__translation-label">' +
                  escapeHtml(decodeHtmlEntities(translationLabel)) +
                  '</p>'
                : '') +
              (canTranslate
                ? '<div class="diggn-reviews__translation-actions">' +
                  '<button class="diggn-reviews__translation-button" type="button" data-diggn-translate-action="' +
                  (showTranslated ? 'original' : 'translate') +
                  '" data-review-id="' +
                  review.id +
                  '"' +
                  (isTranslating ? ' disabled aria-busy="true"' : '') +
                  '>' +
                  escapeHtml(
                    decodeHtmlEntities(
                      isTranslating
                        ? this.strings.translating
                        : showTranslated
                        ? this.strings.showOriginal
                        : this.getTranslateButtonLabel()
                    )
                  ) +
                  '</button>' +
                  '</div>'
                : '') +
              (translationError
                ? '<p class="diggn-reviews__translation-error">' +
                  escapeHtml(decodeHtmlEntities(translationError)) +
                  '</p>'
                : '') +
              (media.length
                ? '<div class="diggn-reviews__media">' +
                  media
                    .map(function (item) {
                      const url = escapeHtml(item.url || item.thumbnailUrl || '');
                      const thumb = escapeHtml(item.thumbnailUrl || item.url || '');
                      return (
                        '<a class="diggn-reviews__media-link" href="' +
                        url +
                        '" target="_blank" rel="noopener">' +
                        '<img src="' +
                        thumb +
                        '" alt="">' +
                        '</a>'
                      );
                    })
                    .join('') +
                  '</div>'
                : '') +
              (hasReply
                ? '<div class="diggn-reviews__reply">' +
                  '<span class="diggn-reviews__badge diggn-reviews__badge--reply">' +
                  escapeHtml(decodeHtmlEntities(this.strings.diggnReply)) +
                  '</span>' +
                  '<p>' +
                  safeReply +
                  '</p>' +
                  '</div>'
                : '') +
              '</article>'
            );
          }.bind(this)
        )
        .join('');
    }

    if (this.loadMoreButton) {
      this.loadMoreButton.hidden = !this.state.hasMore || !this.state.reviews.length;
    }
  };

  DiggnReviews.prototype.setSummaryStatus = function (message, isError) {
    if (!this.summaryStatus) {
      return;
    }

    this.summaryStatus.textContent = message || '';
    this.summaryStatus.hidden = !message;
    this.summaryStatus.classList.toggle('diggn-reviews__status--error', Boolean(isError));
  };

  DiggnReviews.prototype.setResultsStatus = function (message, isError) {
    if (!this.resultsStatus) {
      return;
    }

    this.resultsStatus.textContent = message || '';
    this.resultsStatus.hidden = !message;
    this.resultsStatus.classList.toggle('diggn-reviews__status--error', Boolean(isError));
  };

  DiggnReviews.prototype.openForm = function () {
    if (!this.formPanel) {
      return;
    }

    this.formPanel.hidden = false;
    if (this.formStatus) {
      this.formStatus.textContent = '';
      this.formStatus.classList.remove('diggn-reviews__status--error');
    }
    this.section.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const firstInput = this.formPanel.querySelector('input, textarea, select');
    if (firstInput) {
      window.setTimeout(function () {
        firstInput.focus();
      }, 240);
    }
  };

  DiggnReviews.prototype.submitReview = async function () {
    if (!this.form || !this.formStatus) {
      return;
    }

    const formData = new FormData(this.form);
    const files = Array.from(this.form.querySelector('[data-diggn-review-files]').files || []);

    if (files.length > MAX_UPLOADS) {
      this.formStatus.textContent = this.strings.formFileLimit;
      this.formStatus.classList.add('diggn-reviews__status--error');
      return;
    }

    if (files.some(function (file) { return file.size > MAX_FILE_BYTES; })) {
      this.formStatus.textContent = this.strings.formFileSize;
      this.formStatus.classList.add('diggn-reviews__status--error');
      return;
    }

    this.formStatus.textContent = this.strings.loading;
    this.formStatus.classList.remove('diggn-reviews__status--error');

    try {
      const media = await Promise.all(
        files.map(async function (file) {
          return {
            name: file.name,
            type: file.type,
            size: file.size,
            data: await fileToBase64(file),
            uploadedAt: new Date().toISOString(),
          };
        })
      );

      await requestJson(this.endpoint('submit'), {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify({
          productId: this.productId,
          productHandle: this.productHandle,
          locale: this.locale,
          customerName: formData.get('customer_name'),
          customerEmail: formData.get('customer_email'),
          rating: Number(formData.get('rating')),
          title: formData.get('review_title'),
          body: formData.get('review_body'),
          media: media,
          source: 'shopify_theme',
        }),
      });

      this.form.reset();
      this.formStatus.textContent = this.strings.submitSuccess;
      this.formStatus.classList.remove('diggn-reviews__status--error');
      this.formPanel.hidden = true;
      this.fetchSummary();
      this.fetchReviews(true);
    } catch (error) {
      this.formStatus.textContent = error.message || this.strings.submitError;
      this.formStatus.classList.add('diggn-reviews__status--error');
    }
  };

  document.addEventListener('DOMContentLoaded', function () {
    const sections = document.querySelectorAll('[data-diggn-product-reviews]');

    sections.forEach(function (section) {
      new DiggnReviews(section);
    });
  });
})();
